import { Button, Grid, Box, IconButton, Stack, Typography, TextField as MuiTextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch } from '../../../../../utils';
import { SURGERY_TYPE } from '../../../../../constants/dropdown';

const TABLE_HEADER = [
  {
    header: 'Service Name',
    accessor: 'service_name'
  }
];

export const AddSurgeryDetailsForm = ({
  onClose,
  chargeType = 'opd_charges',
  doctorID,
  onAdd,
  serviceDropdown,
  regionDropdown,
  editData,
  isEditMode
}) => {
  const [input, setInput] = useState('');
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        surgery: Yup.string().required('Please select Service')
      })
    ),
    mode: 'all'
  });
  const { reset } = methods;

  const { handleSubmit, setValue, watch, formState: { errors } } = methods;

  const formSubmitHandler = (formValues) => {
    const surgeryId = serviceDropdown.find(({ _id }) => _id === formValues.surgery)?._id || '';
    const transformedValues = {
      ...formValues,
      surgery: surgeryId
    };

    if (isEditMode) {
      onAdd({ ...transformedValues, _id: editData._id, isEdit: true });
    } else {
      onAdd(transformedValues);
    }
  };

  const handleServiceSelection = (row) => {
    setValue('surgery', row._id);
    setValue('type', row.type);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  useEffect(() => {
    if (isEditMode && editData) {
      reset({
        surgery: editData.surgery,
        region: editData.region,
        sch_time: editData.sch_time,
        duration: editData.duration,
        surgery_type: editData.surgery_type
      });
    } else {
      reset(); // Reset the form when not editing
    }
  }, [isEditMode, editData, reset]);

  useEffect(() => {
    if (!isEditMode) {
      reset();
    }
  }, [isEditMode, reset]);

  const schTime = watch('sch_time');
  const endTime = watch('end_time');

  // Custom validation for end_time
  const validateEndTime = (value) => {
    if (schTime && value && value < schTime) {
      return 'End time must be later than schedule time';
    }
    return true;
  };

  // Effect to update validation for end_time when sch_time changes
  useEffect(() => {
    if (schTime && endTime && endTime < schTime) {
      setValue('end_time', '', { shouldValidate: true }); // Clear end_time if invalid
    }
  }, [schTime, endTime, setValue]);

  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      <Stack
        padding={0}
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="grey.300"
      >
        <Typography variant="h4" fontWeight="bold">
          Add Surgery Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon color="error" fontSize="large" />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
        <MuiTextField
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              padding: '0',
              fontSize: '1.4rem' // Adjust input font size
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 1.4rem'
            },
            '& .MuiInputLabel-root': {
              fontSize: '1.2rem' // Adjust label font size
            }
          }}
          label="Search Service"
          variant="outlined"
          value={input}
          onChange={handleInputChange}
          InputProps={{
            style: {
              fontSize: '12px',
              fontWeight: 600
            }
          }}
        />
      </Stack>
      <FormProvider
        sx={{ height: '100%', overflow: 'auto' }}
        methods={methods}
        onSubmit={handleSubmit(formSubmitHandler)}
      >
        <Grid container spacing={1} sx={{ flex: 1, height: '100%', overflow: 'auto' }}>
          <Grid
            item
            xs={6}
            sx={{
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Table
              containerSX={{ flex: 1, height: '100%', overflow: 'auto' }}
              size="small"
              sx={{ fontSize: '32px' }}
              data={serviceDropdown || []}
              columns={TABLE_HEADER}
              onRowClick={handleServiceSelection}
            />
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, px: 0.5 }}>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Region</Typography>
                <SelectTag
                  formControlSX={{ flex: 0.8 }}
                  menus={regionDropdown.map((data) => ({ ...data, name: data.region }))}
                  name="region"
                  placeholder="Select Region"
                />
              </Stack>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Surgery</Typography>
                <SelectTag
                  formControlSX={{ flex: 0.8 }}
                  name="surgery"
                  menus={serviceDropdown.map((data) => ({ ...data, name: data.service_name }))}
                  placeholder="Select Surgery"
                />
              </Stack>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography sx={{ flex: 0.32, fontWeight: 700, fontSize: '1.4rem' }}>Sch. Time</Typography>
                <TextField
                  type="time"
                  sx={{ flex: 0.3 }}
                  name="sch_time"
                  error={!!errors.sch_time}
                  helperText={errors.sch_time ? errors.sch_time.message : ''}
                />
                <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>End Time</Typography>
                <TextField
                  type="time"
                  sx={{ flex: 0.3 }}
                  name="end_time"
                  error={!!errors.end_time}
                  helperText={errors.end_time ? errors.end_time.message : ''}
                  inputProps={{ step: 300 }} 
                  onChange={(e) => {
                    setValue('end_time', e.target.value, { shouldValidate: true });
                  }}
                />
              </Stack>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography sx={{ flex: 0.32, fontWeight: 700, fontSize: '1.4rem' }}>Surgery Type</Typography>
                <SelectTag menus={SURGERY_TYPE} formControlSX={{ flex: 0.8 }} name="surgery_type" />
              </Stack>
              <Stack spacing={1} direction="row" alignItems="center" justifyContent="flex-end" marginTop="5%">
                <Button variant="contained" type="submit">
                  Add
                </Button>
                <Button variant="outlined" onClick={onClose} color="error">
                  Close
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </Stack>
  );
};
